import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

interface Props {
  /** PNG data URL of the signature, or null while the pad is empty. */
  onChange: (dataUrl: string | null) => void;
  height?: number;
}

/**
 * Sign with a finger (tablet, phone) or the mouse. Pointer events cover
 * both; `touch-action: none` stops the page scrolling while signing.
 */
const SignaturePad: React.FC<Props> = ({ onChange, height = 160 }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  // Sized to its box at (at least) twice its CSS size, so the line stays sharp
  // on screen and in the stored image, even with the browser zoomed out.
  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(2, window.devicePixelRatio || 1);
    const width = canvas.clientWidth;
    if (!width) return;
    const nextWidth = Math.round(width * ratio);
    const nextHeight = Math.round(height * ratio);
    if (canvas.width === nextWidth && canvas.height === nextHeight) return;

    // Resizing a canvas wipes it: keep what was signed and draw it back.
    let previous: HTMLCanvasElement | null = null;
    if (canvas.width && canvas.height) {
      previous = document.createElement("canvas");
      previous.width = canvas.width;
      previous.height = canvas.height;
      previous.getContext("2d")!.drawImage(canvas, 0, 0);
    }
    canvas.width = nextWidth;
    canvas.height = nextHeight;
    const ctx = canvas.getContext("2d")!;
    if (previous) ctx.drawImage(previous, 0, 0, nextWidth, nextHeight);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  }, [height]);

  useEffect(() => {
    setup();
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return;
    // The box can change width after opening (a dialog settling, a tablet turning).
    const observer = new ResizeObserver(() => setup());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [setup]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = e.currentTarget.getContext("2d")!;
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // A dot for a single tap.
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = e.currentTarget.getContext("2d")!;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setEmpty(false);
    onChange(canvasRef.current?.toDataURL("image/png") ?? null);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    // In raw pixels: with the drawing scale applied, clearRect(width, height)
    // would only cover part of the box whenever that scale is not 1.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    ctx.beginPath();
    setEmpty(true);
    onChange(null);
  };

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          border: "1.5px dashed",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "#FAFBFC",
        }}
      >
        <canvas
          ref={canvasRef}
          aria-label="Signature pad: sign here"
          style={{ width: "100%", height, display: "block", touchAction: "none", cursor: "crosshair" }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
        />
        {empty && (
          <Typography
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: "text.disabled",
              pointerEvents: "none",
              fontSize: "0.9rem",
            }}
          >
            Sign here
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button size="small" onClick={clear} disabled={empty} sx={{ textTransform: "none" }}>
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default SignaturePad;
